// Copyright 2020-2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import { generateClient } from 'aws-amplify/api';
import { createAttendeeGraphQL, createMeetingGraphQL, deleteMeetingGraphQL } from '../graphql/mutations';
import { 
  createChimeMeeting,
  getAttendee,
  endChimeMeeting,
  getMeeting,
  joinChimeMeeting,
  startTranscription,
  stopTranscription,
  getAudioMessages,
  deleteAudioMessage,
  sendAudioMessage,
  saveTranscriptionS3,
  getPresignedURL
} from '../graphql/queries';

const client = generateClient();

interface GetAttendeeResponse {
  name: string;
}

export async function createMeeting(title: string, attendeeName: string, region: string, ns_es = false) {
  const joinInfo: any = await client.graphql({
    query: createChimeMeeting,
    variables: {title: title, name: attendeeName, region: region, ns_es: ns_es}
  });
  const joinInfoJson = joinInfo.data.createChimeMeeting;
  const joinInfoJsonParse = JSON.parse(joinInfoJson.body);
  return joinInfoJsonParse;
}

export async function joinMeeting(meetingId: string, name: string) {
  const joinInfo: any = await client.graphql({
    query: joinChimeMeeting,
    variables: {meetingId: meetingId, name: name},
  });
  const joinInfoJson = joinInfo.data.joinChimeMeeting;
  const joinInfoJsonParse = JSON.parse(joinInfoJson.body);
  return joinInfoJsonParse;
}

export async function endMeeting(meetingId: string, title: string) {
  const endInfo: any = await client.graphql({
    query: endChimeMeeting,
    variables: {meetingId: meetingId},
  });
  const endInfoJson = endInfo.data.endChimeMeeting;
  await client.graphql({
    query: deleteMeetingGraphQL,
    variables: {input: { title: title }}
  });
  return endInfoJson;
}

export async function startLiveTranscription(meetingId: string) {
  const transcriptionInfo: any = await client.graphql({
    query: startTranscription,
    variables: {meetingId: meetingId},
  });
  return transcriptionInfo;
}

export async function stopLiveTranscription(meetingId: string) {
  const transcriptionInfo: any = await client.graphql({
    query: stopTranscription,
    variables: {meetingId: meetingId},
  });
  return transcriptionInfo;
}

export async function saveEnTranscription(meetingId: string, transcriptionData: any, timestamp: number) {
  const transcriptionInfo: any = await client.graphql({
    query: saveTranscriptionS3,
    variables: {meetingId: meetingId, transcriptionData: transcriptionData, timestamp: timestamp},
  });
  return transcriptionInfo;
}

export async function getUploadUrl(bucketName: string, keyName: string) {
  const response: any = await client.graphql({
    query: getPresignedURL,
    variables: { bucketName: bucketName, keyName: keyName }
  });
  const responseJson = response.data.getPresignedURL;
  const responseJsonParse = JSON.parse(responseJson.body);
  return responseJsonParse.uploadUrl;
}

export async function fetchAudioMessages() {
  const audioInfo: any = await client.graphql({
    query: getAudioMessages
  });
  const audioInfoJson = audioInfo.data.getAudioMessages;
  const audioInfoJsonParse = JSON.parse(audioInfoJson.body);
  return audioInfoJsonParse;
}

export async function removeAudioMessage(receiptHandle: string) {
  const result: any = await client.graphql({
    query: deleteAudioMessage,
    variables: {receiptHandle: receiptHandle},
  });
  console.log('Message deleted:', result.data.deleteAudioMessage);
}

export async function passAudioMessage(base64Audio: string, meetingId: string) {
  const result: any = await client.graphql({
    query: sendAudioMessage,
    variables: {
      base64Audio: base64Audio,
      meetingId: meetingId
    },
  });
}

export async function addMeetingToDB(title: string, meetingId: string, meetingData: string) {
  await client.graphql({
    query: createMeetingGraphQL,
    variables: {input: {title: title, meetingId: meetingId, data: meetingData, }}
  });
}

export async function addAttendeeToDB(attendeeID: string, attendeeName: string) {
  await client.graphql({
    query: createAttendeeGraphQL,
    variables: {input: {attendeeId: attendeeID, name: attendeeName }}
  });
}

export async function getMeetingFromDB(title: string) {
  const meetingInfo = await client.graphql({
    query: getMeeting,
    variables: { title: title }
  });
  return meetingInfo;
}

export async function getAttendeeFromDB(title: string, attendeeId: string): Promise<GetAttendeeResponse> {
  const attendeeInfo:any = await client.graphql({
    query: getAttendee,
    variables: {attendeeId: `${title}/${attendeeId}` }
  });
  if (!attendeeInfo.data.getAttendee) {
    return {
      name: 'Unknown'
    }
  }
  return {
    name: attendeeInfo.data.getAttendee.name
  }
}

export const createGetAttendeeCallback = (meetingId: string) => (
  chimeAttendeeId: string
): Promise<GetAttendeeResponse> => getAttendeeFromDB(meetingId, chimeAttendeeId);